import { createSlice } from '@reduxjs/toolkit';
import SmsWalletService from '../../services/smsWallet';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  transactions: [],
  transaction: null,
  totalCount: 0,
  pagination: null
};

const slice = createSlice({
  name: 'smsWallet',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET TRANSACTIONS SUCCESS
    getTransactionsSuccess(state, action) {
      state.isLoading = false;
      state.transactions = action.payload.data || [];
      state.totalCount = action.payload.pagination?.totalItems || 0;
      state.pagination = action.payload.pagination || null;
    },

    // GET TRANSACTION SUCCESS
    getTransactionSuccess(state, action) {
      state.isLoading = false;
      state.transaction = action.payload.data || null;
    },

    // APPROVE TRANSACTION SUCCESS
    approveTransactionSuccess(state, action) {
      state.isLoading = false;
      const updatedTransaction = action.payload.data;
      if (updatedTransaction) {
        const index = state.transactions.findIndex((txn) => txn.id === updatedTransaction.id);
        if (index !== -1) {
          state.transactions[index] = {
            ...state.transactions[index],
            status: 'approved',
            ...updatedTransaction
          };
        }
        if (state.transaction && state.transaction.id === updatedTransaction.id) {
          state.transaction = {
            ...state.transaction,
            status: 'approved',
            ...updatedTransaction
          };
        }
      }
    },

    // REJECT TRANSACTION SUCCESS
    rejectTransactionSuccess(state, action) {
      state.isLoading = false;
      const updatedTransaction = action.payload.data;
      if (updatedTransaction) {
        const index = state.transactions.findIndex((txn) => txn.id === updatedTransaction.id);
        if (index !== -1) {
          state.transactions[index] = {
            ...state.transactions[index],
            status: 'rejected',
            ...updatedTransaction
          };
        }
        if (state.transaction && state.transaction.id === updatedTransaction.id) {
          state.transaction = {
            ...state.transaction,
            status: 'rejected',
            ...updatedTransaction
          };
        }
      }
    },

    // CLEAR TRANSACTION
    clearTransaction(state) {
      state.transaction = null;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  startLoading,
  hasError,
  getTransactionsSuccess,
  getTransactionSuccess,
  approveTransactionSuccess,
  rejectTransactionSuccess,
  clearTransaction
} = slice.actions;

// ----------------------------------------------------------------------

export function getAdminTransactions(params = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await SmsWalletService.getAdminList(params);
      if (response.success) {
        dispatch(slice.actions.getTransactionsSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.errors || 'Failed to fetch transactions'));
      }
    } catch (error) {
      console.error('Get transactions error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch transactions'));
    }
  };
}

export function getTransaction(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await SmsWalletService.getTransactionById(id);
      if (response.success) {
        dispatch(slice.actions.getTransactionSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.errors || 'Failed to fetch transaction'));
      }
    } catch (error) {
      console.error('Get transaction error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to fetch transaction'));
    }
  };
}

export function approveTransaction(transactionId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await SmsWalletService.approveTopup(transactionId);
      if (response.success) {
        dispatch(slice.actions.approveTransactionSuccess(response));
        return { success: true, data: response.data };
      }
      dispatch(slice.actions.hasError(response.errors || 'Failed to approve transaction'));
      return { success: false, errors: response.errors };
    } catch (error) {
      console.error('Approve transaction error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to approve transaction'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}

export function rejectTransaction(transactionId, reason = '') {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await SmsWalletService.rejectTopup(transactionId, reason);
      if (response.success) {
        dispatch(slice.actions.rejectTransactionSuccess(response));
        return { success: true, data: response.data };
      }
      dispatch(slice.actions.hasError(response.errors || 'Failed to reject transaction'));
      return { success: false, errors: response.errors };
    } catch (error) {
      console.error('Reject transaction error:', error);
      dispatch(slice.actions.hasError(error.message || 'Failed to reject transaction'));
      return { success: false, errors: [{ message: error.message }] };
    }
  };
}
